import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DEFAULT_LANGUAGE, PrimeNGTranslationLoader, SUPPORTED_LANGUAGE_CODES, SupportedLanguageCode } from '@config';
import { environment } from '@env/environment';
import { TranslocoService } from '@jsverse/transloco';
import { PrimeNG } from 'primeng/config';
import { filter } from 'rxjs';

/**
 * Service responsible for synchronizing PrimeNG component translations with Transloco language changes.
 * Automatically updates PrimeNG translations when the application language is changed.
 */
@Injectable({
  providedIn: 'root',
})
export class PrimeNGTranslationService {
  private primeNG = inject(PrimeNG);
  private translocoService = inject(TranslocoService);

  constructor() {
    // Subscribe to translation events to catch all language changes
    this.translocoService.events$
      .pipe(
        takeUntilDestroyed(),
        filter((event) => event.type === 'langChanged'),
      )
      .subscribe((event) => {
        this.updatePrimeNGTranslations(event.payload.langName);
      });

    // Initialize with current language
    const currentLang = this.translocoService.getActiveLang();
    if (currentLang) {
      this.updatePrimeNGTranslations(currentLang);
    }
  }

  private async updatePrimeNGTranslations(lang: string): Promise<void> {
    const supportedLang = this.getSupportedLanguage(lang);

    try {
      const translation = await PrimeNGTranslationLoader.loadTranslation(supportedLang);
      this.primeNG.setTranslation(translation);
    } catch (error) {
      if (!environment.production) {
        console.error('Failed to load PrimeNG translation for:', supportedLang, error);
      }
      const fallback = await PrimeNGTranslationLoader.loadTranslation(DEFAULT_LANGUAGE as SupportedLanguageCode);
      this.primeNG.setTranslation(fallback);
    }
  }

  private getSupportedLanguage(lang: string): SupportedLanguageCode {
    return SUPPORTED_LANGUAGE_CODES.includes(lang as SupportedLanguageCode)
      ? (lang as SupportedLanguageCode)
      : (DEFAULT_LANGUAGE as SupportedLanguageCode);
  }

  public async updateTranslations(): Promise<void> {
    const currentLang = this.translocoService.getActiveLang();
    await this.updatePrimeNGTranslations(currentLang);
  }
}
